<template>
  <div class="video-lessons container py-4">
    <h2 class="text-pink mb-4">Video Lessons</h2>

    <!-- Dropdown Wrapper -->
    <div class="dropdown-section mb-4">
      <DropdownItem @topic-selected="fetchVideosByTopic" />
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-pink" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-pink">Loading videos...</p>
    </div>

    <!-- Videos -->
    <div v-else-if="videos.length" class="row g-2">
      <div
        v-for="video in videos"
        :key="video.id"
        class="col-12 col-sm-6 col-md-4 col-lg-3"
      >
        <div class="card bg-dark text-white border-pink h-100 small-card">
          <!-- Video or Thumbnail -->
          <div class="card-img-top small-card-img position-relative">
            <template v-if="video.playing">
              <iframe
                :src="`https://www.youtube.com/embed/${video.video_id}?autoplay=1&rel=0`"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                class="w-100 h-100"
              ></iframe>
            </template>
            <template v-else>
              <img
                :src="getYoutubeThumbnail(video.video_id)"
                alt="thumbnail"
                class="w-100 h-100"
                @click="playVideo(video)"
                style="cursor: pointer"
              />
              <div class="play-button-overlay" @click="playVideo(video)">
                <i class="bi bi-play-fill fs-3"></i>
              </div>
            </template>
          </div>

          <!-- Card Body -->
          <div class="card-body p-2">
            <h6 class="card-title text-pink mb-1">{{ video.title }}</h6>
            <p class="card-text small mb-1">
              <strong>Subject:</strong> {{ video.subject_name }} <br />
              <strong>Grade:</strong> {{ video.grade_level }}
            </p>
            <button
              v-if="!video.playing"
              class="btn btn-pink btn-sm w-100"
              @click="playVideo(video)"
            >
              <i class="bi bi-youtube"></i> Watch
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- No videos -->
    <div v-else class="text-center py-4 text-pink">
      <i class="bi bi-camera-video-off fs-2"></i>
      <div class="mt-2">No Video Lessons Assigned Yet.</div>
      <small>Enroll in subjects or check with your teacher.</small>
    </div>
  </div>
</template>

<script>
import DropdownItem from "@/components/StudentDashboard/StudentVideoPart/DropdownItem.vue";
import axios from "axios";

export default {
  name: "VideoLessons",

  components: {
    DropdownItem,
  },

  data() {
    return {
      videos: [],
      loading: false,
    };
  },

  methods: {
    getYoutubeThumbnail(videoId) {
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    },

    playVideo(video) {
      // Stop all other videos first
      this.videos.forEach((v) => (v.playing = false));
      video.playing = true;
    },

    getYoutubeUrl(videoId) {
      return `https://www.youtube.com/watch?v=${videoId}`;
    },

    async fetchVideosByTopic(topicId) {
      if (!topicId) return;

      this.loading = true;

      try {
        const token = localStorage.getItem("token");

        const res = await axios.get(`/student-videos/${topicId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        this.videos = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        console.error(err);
        this.videos = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.dropdown-section {
  margin-bottom: 1.5rem;
}

@media (min-width: 768px) {
  .dropdown-section {
    margin-bottom: 2rem;
  }
}
.text-pink {
  color: #ff00cc !important;
}

.btn-pink {
  background-color: #ff00cc;
  color: white;
  border: none;
  font-size: 0.8rem;
  padding: 4px 8px;
}

.btn-pink:hover {
  background-color: #e600b3;
  color: white;
}

.border-pink {
  border-color: #ff00cc !important;
}

/* Page spacing */
.video-lessons {
  max-width: 1400px;
}

/* Cards */
.small-card {
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(255, 0, 204, 0.25);
  transition: all 0.2s ease;
  overflow: hidden;
  background: #111;
}

.small-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(255, 0, 204, 0.45);
}

/* Video container (16:9 ratio) */
.small-card-img {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 0, 204, 0.3);
}

.small-card-img img,
.small-card-img iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: none;
}

/* Play button */
.play-button-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  padding: 12px 14px;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
}

.play-button-overlay:hover {
  background: rgba(0, 0, 0, 0.8);
}

/* Card body */
.card-body {
  padding: 8px;
}

.card-title {
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.card-text.small {
  font-size: 0.75rem;
}

/* Tablet */
@media (max-width: 992px) {
  .card-title {
    font-size: 0.85rem;
  }

  .card-text.small {
    font-size: 0.7rem;
  }
}

/* Mobile */
@media (max-width: 576px) {
  .video-lessons {
    padding-left: 10px;
    padding-right: 10px;
  }

  .card-title {
    font-size: 0.8rem;
  }

  .card-text.small {
    font-size: 0.68rem;
  }

  .play-button-overlay {
    padding: 10px 12px;
  }
}
</style>
